import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptions as api, vendors as vendorApi, companies as companyApi } from '@/api/endpoints';
import { hasPermission } from '@/types';
import type {
  Company, Subscription, SubscriptionInput, SubscriptionMeta,
  SubscriptionCategoryCode, SubscriptionStatusCode, Vendor, VendorInput,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { formatCurrency } from '@/lib/money';

const URGENCY_ROW: Record<string, string> = {
  OVERDUE: 'border-l-2 border-l-red-500',
  DUE_SOON: 'border-l-2 border-l-amber-500',
  OK: '',
  SETTLED: '',
};

/** „za 12 dni" / „dziś" / „14 dni po terminie" — jedna etykieta zamiast surowej liczby. */
function dniLabel(days?: number | null): string {
  if (days == null) return '—';
  if (days === 0) return 'dziś';
  if (days > 0) return `za ${days} ${days === 1 ? 'dzień' : 'dni'}`;
  const over = -days;
  return `${over} ${over === 1 ? 'dzień' : 'dni'} po terminie`;
}

/**
 * Subskrypcje i płatności cykliczne (V153): OC firmy, podatki, licencje, abonamenty.
 * Lista odpowiada od razu na dwa pytania — co trzeba zapłacić i gdzie brakuje faktury.
 * Szczegóły okresów i przypinanie faktur są na /subskrypcje/:id.
 */
export function SubskrypcjePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(user, 'FINANCE_MANAGE');

  const [status, setStatus] = useState<SubscriptionStatusCode | ''>('ACTIVE');
  const [category, setCategory] = useState<SubscriptionCategoryCode | ''>('');
  const [rows, setRows] = useState<Subscription[] | null>(null);
  const [meta, setMeta] = useState<SubscriptionMeta | null>(null);
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Subscription | 'new' | null>(null);
  const [vendorsOpen, setVendorsOpen] = useState(false);

  const reload = () =>
    api.list({ status: status || undefined, category: category || undefined })
      .then(setRows)
      .catch(e => setErr(e?.message ?? String(e)));

  const reloadVendors = () => vendorApi.list().then(setVendorList).catch(() => {});

  useEffect(() => { setRows(null); setErr(null); reload(); /* eslint-disable-next-line */ }, [status, category]);
  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    reloadVendors();
    companyApi.list().then(setCompanyList).catch(() => {});
  }, []);

  // Podsumowanie liczone z widocznych wierszy — sumy kwot osobno per waluta, bo systemu kursów nie ma.
  const summary = useMemo(() => {
    const list = rows ?? [];
    const overdue = list.reduce((a, s) => a + s.overduePeriods, 0);
    const missing = list.reduce((a, s) => a + s.missingInvoices, 0);
    const perCurrency = new Map<string, number>();
    for (const s of list) {
      if (s.status !== 'ACTIVE' || s.yearlyAmount == null) continue;
      perCurrency.set(s.currency, (perCurrency.get(s.currency) ?? 0) + s.yearlyAmount);
    }
    return { overdue, missing, perCurrency: [...perCurrency.entries()] };
  }, [rows]);

  const remove = (s: Subscription, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Usunąć subskrypcję „${s.name}"?\nOkresy rozliczeniowe znikną razem z nią. Faktury zostaną w module Faktury.`)) return;
    api.remove(s.id).then(reload).catch(er => setErr(er?.message ?? String(er)));
  };

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Subskrypcje i płatności cykliczne</h1>
          <div className="text-ink-400 text-[13px]">
            OC firmy, podatki, licencje, abonamenty — terminy, kwoty i faktury w jednym miejscu
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setVendorsOpen(true)} className="btn btn-secondary btn-sm">
            <Icon name="users" className="w-3.5 h-3.5"/> Dostawcy ({vendorList.length})
          </button>
          {canManage && (
            <button onClick={() => setEditing('new')} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowa subskrypcja
            </button>
          )}
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      {rows && rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 text-[12px]">
          {summary.overdue > 0 && (
            <span className="pill pill-red">
              {summary.overdue} {summary.overdue === 1 ? 'okres po terminie' : 'okresów po terminie'}
            </span>
          )}
          {summary.missing > 0 && (
            <span className="pill pill-amber">
              brak faktury: {summary.missing} {summary.missing === 1 ? 'okres' : 'okresów'}
            </span>
          )}
          {summary.perCurrency.map(([cur, sum]) => (
            <span key={cur} className="pill pill-earth" title="Suma kwot rocznych aktywnych subskrypcji w tej walucie">
              rocznie: {formatCurrency(sum, cur)}
            </span>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-head flex-wrap gap-2">
          <div className="flex gap-1 bg-ink-50 p-1 rounded-md">
            <button onClick={() => setStatus('ACTIVE')} className={'btn btn-sm ' + (status === 'ACTIVE' ? 'btn-primary' : 'btn-ghost')}>Aktywne</button>
            <button onClick={() => setStatus('PAUSED')} className={'btn btn-sm ' + (status === 'PAUSED' ? 'btn-primary' : 'btn-ghost')}>Wstrzymane</button>
            <button onClick={() => setStatus('ENDED')} className={'btn btn-sm ' + (status === 'ENDED' ? 'btn-primary' : 'btn-ghost')}>Zakończone</button>
            <button onClick={() => setStatus('')} className={'btn btn-sm ' + (status === '' ? 'btn-primary' : 'btn-ghost')}>Wszystkie</button>
          </div>
          <div className="w-60 ml-auto">
            <SearchableSelect value={category} onChange={v => setCategory(v as SubscriptionCategoryCode | '')} placeholder="Wszystkie kategorie">
              <option value="">Wszystkie kategorie</option>
              {(meta?.categories ?? []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </SearchableSelect>
          </div>
        </div>
        <div className="card-body !p-0">
          {rows == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="p-6 text-center text-ink-400 text-[12px]">
              <Icon name="calendar" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
              Brak subskrypcji w tym widoku.
              {canManage && <> Kliknij <strong>Nowa subskrypcja</strong>, aby dodać pierwszą (np. OC firmy).</>}
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Dostawca</th>
                  <th>Kategoria</th>
                  <th>Cykl</th>
                  <th className="text-right">Kwota</th>
                  <th className="text-right">Rocznie</th>
                  <th>Następny termin</th>
                  <th>Uwagi</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id}
                      className={'hover:bg-ink-50 cursor-pointer ' + (URGENCY_ROW[s.urgency] ?? '')}
                      title="Otwórz okresy rozliczeniowe i faktury"
                      onClick={() => navigate(`/subskrypcje/${s.id}`)}>
                    <td className="text-[13px] text-ink-900">
                      <div className="font-semibold truncate max-w-[260px]" title={s.name}>{s.name}</div>
                      {s.referenceNumber && (
                        <div className="font-mono text-[10px] text-ink-400" title="Numer polisy / umowy">
                          {s.referenceNumber}
                        </div>
                      )}
                      {s.status !== 'ACTIVE' && (
                        <span className={'pill pill-' + s.statusColor + ' text-[10px] mt-0.5 inline-block'}>{s.statusName}</span>
                      )}
                    </td>
                    <td className="text-[13px] text-ink-800">
                      <div className="truncate max-w-[200px]" title={s.vendorName ?? undefined}>{s.vendorName ?? '—'}</div>
                      {s.vendorForeign && (
                        <span className="pill pill-ai text-[10px]" title="Dostawca zagraniczny — faktury nie ma w KSeF, wgraj PDF ręcznie">
                          zagraniczny
                        </span>
                      )}
                    </td>
                    <td><span className={'pill pill-' + s.categoryColor}>{s.categoryName}</span></td>
                    <td className="text-[12px] text-ink-600">{s.billingCycleName}</td>
                    <td className="text-right font-mono text-[13px] text-ink-900">{formatCurrency(s.plannedAmount, s.currency)}</td>
                    <td className="text-right font-mono text-[12px] text-ink-500">{formatCurrency(s.yearlyAmount, s.currency)}</td>
                    <td className="font-mono text-[12px]">
                      {s.nextDueDate ? (
                        <>
                          <div className={s.urgency === 'OVERDUE' ? 'text-red-600 font-semibold' : 'text-ink-700'}>
                            {s.nextDueDate}
                          </div>
                          <div className={'text-[10px] ' + (s.urgency === 'OVERDUE' ? 'text-red-500' : 'text-ink-400')}>
                            {dniLabel(s.daysUntil)}
                          </div>
                        </>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-[11px]">
                      <div className="flex flex-col gap-0.5 items-start">
                        {s.missingInvoices > 0 && (
                          <span className="pill pill-amber" title="Okresy po terminie bez przypiętej faktury">
                            brak faktury ×{s.missingInvoices}
                          </span>
                        )}
                        {!s.invoiceExpected && (
                          <span className="pill pill-gray" title="Dla tej pozycji nie oczekujemy faktury (podatek, składka)">
                            bez faktury
                          </span>
                        )}
                        {s.paymentAutomatic && (
                          <span className="pill pill-water" title={s.paymentMethodName}>automatyczna</span>
                        )}
                      </div>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {canManage && (
                        <>
                          <button onClick={e => { e.stopPropagation(); setEditing(s); }}
                                  className="text-water-700 hover:text-water-900 px-1" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5 inline"/>
                          </button>
                          <button onClick={e => remove(s, e)}
                                  className="text-ink-400 hover:text-red-600 px-1" title="Usuń subskrypcję">
                            <Icon name="x" className="w-3.5 h-3.5 inline"/>
                          </button>
                        </>
                      )}
                      <Icon name="chevron" className="w-3 h-3 text-ink-400 inline"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing && meta && (
        <SubscriptionModal
          value={editing === 'new' ? null : editing}
          meta={meta}
          vendors={vendorList}
          companies={companyList}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
          onVendorAdded={reloadVendors}
        />
      )}

      {vendorsOpen && (
        <VendorsModal
          canManage={canManage}
          onClose={() => { setVendorsOpen(false); reload(); }}
          onChanged={reloadVendors}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Formularz subskrypcji
// ═══════════════════════════════════════════════════════════════════════════

function SubscriptionModal({ value, meta, vendors, companies, onClose, onSaved, onVendorAdded }: {
  value: Subscription | null;
  meta: SubscriptionMeta;
  vendors: Vendor[];
  companies: Company[];
  onClose: () => void;
  onSaved: () => void;
  onVendorAdded: () => void;
}) {
  const [form, setForm] = useState<SubscriptionInput>(() => value ? {
    name: value.name,
    category: value.category,
    referenceNumber: value.referenceNumber ?? '',
    description: value.description ?? '',
    vendorId: value.vendorId!,
    ourCompanyId: value.ourCompanyId ?? null,
    billingCycle: value.billingCycle,
    startDate: value.startDate,
    endDate: value.endDate ?? null,
    plannedAmount: value.plannedAmount ?? null,
    currency: value.currency,
    paymentMethod: value.paymentMethod,
    remindDaysBefore: value.remindDaysBefore ?? 30,
    invoiceExpected: value.invoiceExpected,
    autoRenew: value.autoRenew,
    status: value.status,
    notes: value.notes ?? '',
  } : {
    name: '',
    category: 'INSURANCE',
    referenceNumber: '',
    description: '',
    vendorId: 0,
    ourCompanyId: null,
    billingCycle: 'YEARLY',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: null,
    plannedAmount: null,
    currency: 'PLN',
    paymentMethod: 'TRANSFER',
    remindDaysBefore: 30,
    invoiceExpected: true,
    autoRenew: true,
    status: 'ACTIVE',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newVendor, setNewVendor] = useState(false);

  const set = <K extends keyof SubscriptionInput>(k: K, v: SubscriptionInput[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  /** Zmiana kategorii podpowiada „czy oczekujemy faktury" — tylko przy zakładaniu nowej pozycji. */
  const onCategory = (code: SubscriptionCategoryCode) => {
    const def = meta.categories.find(c => c.id === code);
    setForm(f => ({
      ...f,
      category: code,
      invoiceExpected: value ? f.invoiceExpected : (def?.invoiceExpectedByDefault ?? true),
    }));
  };

  const save = () => {
    setBusy(true); setErr(null);
    const payload: SubscriptionInput = { ...form, vendorId: Number(form.vendorId) };
    const p = value ? api.update(value.id, payload) : api.create(payload);
    p.then(onSaved).catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div className="font-semibold text-[14px]">{value ? 'Edytuj subskrypcję' : 'Nowa subskrypcja'}</div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-[12px]">{err}</div>}

          <label className="field"><span className="field-label">Nazwa *</span>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
                   placeholder="np. OC działalności — Warta"/></label>

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Kategoria *</span>
              <SearchableSelect value={form.category} onChange={v => onCategory(v as SubscriptionCategoryCode)}>
                {meta.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Numer polisy / umowy</span>
              <input className="input" value={form.referenceNumber ?? ''} onChange={e => set('referenceNumber', e.target.value)}/></label>
          </div>

          <div className="field">
            <div className="flex items-center">
              <span className="field-label">Dostawca *</span>
              <button type="button" onClick={() => setNewVendor(true)}
                      className="ml-auto text-[11px] text-water-700 hover:underline">+ nowy dostawca</button>
            </div>
            <SearchableSelect value={form.vendorId ? String(form.vendorId) : ''} onChange={v => set('vendorId', Number(v))}
                              placeholder="Wybierz dostawcę">
              <option value="">— wybierz —</option>
              {vendors.map(v => (
                <option key={v.id} value={String(v.id)}>
                  {v.name}{v.foreign ? ` (${v.country})` : ''}{v.active ? '' : ' — nieaktywny'}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Cykl *</span>
              <SearchableSelect value={form.billingCycle} onChange={v => set('billingCycle', v as SubscriptionInput['billingCycle'])}>
                {meta.cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect></label>
            <label className="field"><span className="field-label">Początek *</span>
              <input type="date" className="input" value={form.startDate} onChange={e => set('startDate', e.target.value)}/></label>
            <label className="field"><span className="field-label">Koniec</span>
              <input type="date" className="input" value={form.endDate ?? ''} onChange={e => set('endDate', e.target.value || null)}/></label>
          </div>
          <div className="field-hint -mt-1">
            Kolejne okresy liczone są od daty początku (kotwica kalendarzowa) — spóźniona zapłata nie przesuwa następnych terminów.
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="field"><span className="field-label">Kwota za okres</span>
              <input type="number" step="0.01" min="0" className="input"
                     value={form.plannedAmount ?? ''}
                     onChange={e => set('plannedAmount', e.target.value === '' ? null : Number(e.target.value))}
                     placeholder="puste = zmienna"/></label>
            <label className="field"><span className="field-label">Waluta</span>
              <input className="input uppercase" maxLength={3} value={form.currency ?? 'PLN'}
                     onChange={e => set('currency', e.target.value.toUpperCase())}/></label>
            <label className="field"><span className="field-label">Sposób zapłaty</span>
              <SearchableSelect value={form.paymentMethod ?? 'TRANSFER'} onChange={v => set('paymentMethod', v as SubscriptionInput['paymentMethod'])}>
                {meta.paymentMethods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </SearchableSelect></label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">Przypomnij na ile dni przed</span>
              <input type="number" min="0" className="input" value={form.remindDaysBefore ?? 30}
                     onChange={e => set('remindDaysBefore', Number(e.target.value))}/></label>
            <label className="field"><span className="field-label">Nasza firma (płatnik)</span>
              <SearchableSelect value={form.ourCompanyId ? String(form.ourCompanyId) : ''}
                                onChange={v => set('ourCompanyId', v ? Number(v) : null)}>
                <option value="">—</option>
                {companies.map(c => <option key={c.id} value={String(c.id)}>{c.shortName ?? c.fullName}</option>)}
              </SearchableSelect></label>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-[12px] cursor-pointer"
                   title="Odznacz dla podatków i składek — wyłącza alert „minął termin, brak faktury”">
              <input type="checkbox" className="w-4 h-4" checked={form.invoiceExpected ?? true}
                     onChange={e => set('invoiceExpected', e.target.checked)}/>
              Oczekujemy faktury
            </label>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={form.autoRenew ?? true}
                     onChange={e => set('autoRenew', e.target.checked)}/>
              Odnawia się automatycznie
            </label>
            {value && (
              <div className="ml-auto w-40">
                <SearchableSelect value={form.status ?? 'ACTIVE'} onChange={v => set('status', v as SubscriptionStatusCode)}>
                  {meta.statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </SearchableSelect>
              </div>
            )}
          </div>

          <label className="field"><span className="field-label">Notatki</span>
            <textarea className="input" rows={2} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)}/></label>
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy || !form.name.trim() || !form.vendorId} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>

      {newVendor && (
        <VendorFormModal
          value={null}
          onClose={() => setNewVendor(false)}
          onSaved={v => { setNewVendor(false); onVendorAdded(); set('vendorId', v.id); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Dostawcy
// ═══════════════════════════════════════════════════════════════════════════

function VendorsModal({ canManage, onClose, onChanged }: {
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<Vendor[] | null>(null);
  const [editing, setEditing] = useState<Vendor | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = () => vendorApi.list().then(r => { setRows(r); onChanged(); }).catch(e => setErr(String(e)));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const remove = (v: Vendor) => {
    if (!confirm(`Usunąć dostawcę „${v.name}"?`)) return;
    setErr(null);
    vendorApi.remove(v.id).then(reload).catch(e => setErr(e?.message ?? String(e)));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center gap-2">
          <div className="font-semibold text-[14px]">Dostawcy usług cyklicznych</div>
          {canManage && (
            <button onClick={() => setEditing('new')} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj
            </button>
          )}
          <button onClick={onClose} className={(canManage ? '' : 'ml-auto ') + 'text-ink-400 hover:text-ink-700'}>
            <Icon name="x" className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-0">
          {err && <div className="p-3 text-red-700 bg-red-50 border-b border-red-200 text-[12px]">{err}</div>}
          {rows == null && <div className="p-4 text-ink-400 text-[12px]">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="p-6 text-center text-ink-400 text-[12px]">
              Brak dostawców. Dodaj ubezpieczyciela, urząd albo producenta oprogramowania.
            </div>
          )}
          {rows && rows.length > 0 && (
            // overflow-x-auto — tabela w modalu nie może wylewać się poza jego ramkę.
            <div className="overflow-x-auto">
            <table className="table-hw">
              <thead>
                <tr><th>Nazwa</th><th>NIP / VAT</th><th>Kraj</th><th>Kontakt</th><th>Klient CRM</th><th></th></tr>
              </thead>
              <tbody>
                {rows.map(v => (
                  <tr key={v.id} className={v.active ? '' : 'opacity-50'}>
                    <td className="text-[13px] font-semibold text-ink-900">
                      {v.name}
                      {!v.active && <span className="pill pill-gray text-[10px] ml-1">nieaktywny</span>}
                    </td>
                    <td className="font-mono text-[12px] text-ink-600">{v.taxId ?? '—'}</td>
                    <td className="text-[12px]">
                      {v.foreign
                        ? <span className="pill pill-ai" title="Faktury nie ma w KSeF — wgraj PDF ręcznie">{v.country}</span>
                        : <span className="text-ink-500">PL</span>}
                    </td>
                    <td className="text-[11px] text-ink-500">
                      {v.contactEmail ?? v.phone ?? v.website ?? '—'}
                    </td>
                    <td className="text-[12px] text-ink-600">{v.clientName ?? '—'}</td>
                    <td className="text-right whitespace-nowrap">
                      {canManage && (
                        <>
                          <button onClick={() => setEditing(v)} className="text-water-700 hover:text-water-900 px-1" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5 inline"/>
                          </button>
                          <button onClick={() => remove(v)} className="text-ink-400 hover:text-red-600 px-1" title="Usuń">
                            <Icon name="x" className="w-3.5 h-3.5 inline"/>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <VendorFormModal
          value={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function VendorFormModal({ value, onClose, onSaved }: {
  value: Vendor | null;
  onClose: () => void;
  onSaved: (v: Vendor) => void;
}) {
  const [form, setForm] = useState<VendorInput>(() => value ? {
    name: value.name, taxId: value.taxId ?? '', country: value.country,
    website: value.website ?? '', contactEmail: value.contactEmail ?? '', phone: value.phone ?? '',
    notes: value.notes ?? '', active: value.active, clientId: value.clientId ?? null,
  } : {
    name: '', taxId: '', country: 'PL', website: '', contactEmail: '', phone: '',
    notes: '', active: true, clientId: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof VendorInput>(k: K, v: VendorInput[K]) => setForm(f => ({ ...f, [k]: v }));

  const save = () => {
    setBusy(true); setErr(null);
    const p = value ? vendorApi.update(value.id, form) : vendorApi.create(form);
    p.then(onSaved).catch(e => { setBusy(false); setErr(e?.message ?? String(e)); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: form, onClose, busy,
  });

  return (
    // stopPropagation na overlayu: ten modal bywa dzieckiem overlaya modala subskrypcji/dostawców,
    // więc bez tego kliknięcie w jego tło bąbelkowało wyżej i zamykało TAKŻE formularz nadrzędny,
    // kasując wpisane dane.
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={e => { e.stopPropagation(); requestClose(); }} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg my-6" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <div className="font-semibold text-[14px]">{value ? 'Edytuj dostawcę' : 'Nowy dostawca'}</div>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-2 text-[12px]">{err}</div>}
          <label className="field"><span className="field-label">Nazwa *</span>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
                   placeholder="np. TUiR Warta S.A. / Adobe Systems Software Ireland"/></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="field col-span-2"><span className="field-label">NIP / VAT UE</span>
              <input className="input" value={form.taxId ?? ''} onChange={e => set('taxId', e.target.value)}
                     placeholder="5210082866 albo IE6364992H"/></label>
            <label className="field"><span className="field-label">Kraj</span>
              <input className="input uppercase" maxLength={2} value={form.country ?? 'PL'}
                     onChange={e => set('country', e.target.value.toUpperCase())}/></label>
          </div>
          <div className="field-hint -mt-1">
            Kraj inny niż PL oznacza, że faktury nie będzie w KSeF — trzeba ją pobrać i wgrać ręcznie.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="field"><span className="field-label">E-mail</span>
              <input className="input" value={form.contactEmail ?? ''} onChange={e => set('contactEmail', e.target.value)}/></label>
            <label className="field"><span className="field-label">Telefon</span>
              <input className="input" value={form.phone ?? ''} onChange={e => set('phone', e.target.value)}/></label>
          </div>
          <label className="field"><span className="field-label">Strona / panel klienta</span>
            <input className="input" value={form.website ?? ''} onChange={e => set('website', e.target.value)}
                   placeholder="tam pobierzesz fakturę zagraniczną"/></label>
          <label className="field"><span className="field-label">Notatki</span>
            <textarea className="input" rows={2} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)}/></label>
          <label className="flex items-center gap-2 text-[12px] cursor-pointer">
            <input type="checkbox" className="w-4 h-4" checked={form.active ?? true}
                   onChange={e => set('active', e.target.checked)}/>
            Aktywny
          </label>
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button onClick={save} disabled={busy || !form.name.trim()} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}
